import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { listMatrix, listRequirements, listVerifications } from '@/api/client';
import type { MatrixLink, Requirement, Verification } from '@/api/types';
import {
  COLUMN_X,
  ROW_HEIGHT,
  TOP_PADDING,
  nodeTypes,
} from '@/components/graph/nodes';
import ResizableGraphShell from '@/components/graph/ResizableGraphShell';
import { useGraphNodeNavigation } from '@/hooks/useGraphNodeNavigation';
import { highlightEdgesForSelection, highlightNodesForSelection } from '@/utils/graphHighlight';

function buildGraph(
  matrix: MatrixLink[],
  requirements: Requirement[],
  verifications: Verification[],
  projectId: number,
): { nodes: Node[]; edges: Edge[] } {
  const reqById = new Map(requirements.map((r) => [r.id, r]));
  const verById = new Map(verifications.map((v) => [v.id, v]));

  const reqIds = new Set<number>();
  const verIds = new Set<number>();
  for (const m of matrix) {
    if (m.project_id !== projectId) continue;
    reqIds.add(m.req_id);
    verIds.add(m.verification_id);
  }

  const nodes: Node[] = [];
  let yi = 0;
  for (const rid of reqIds) {
    const r = reqById.get(rid);
    const label = r?.title ?? `Requirement ${rid}`;
    const idStr = r?.reference_code ?? `REQ-${rid}`;
    nodes.push({
      id: `r-${rid}`,
      type: 'requirement',
      position: { x: COLUMN_X.requirement, y: TOP_PADDING + yi * ROW_HEIGHT.requirement },
      data: {
        kind: 'requirement',
        id: idStr,
        label,
        statusLine: r?.approval_state ?? '',
      },
    });
    yi += 1;
  }

  let yj = 0;
  for (const vid of verIds) {
    const v = verById.get(vid);
    nodes.push({
      id: `v-${vid}`,
      type: 'verification',
      position: { x: COLUMN_X.verification, y: TOP_PADDING + yj * ROW_HEIGHT.verification },
      data: {
        kind: 'verification',
        id: String(vid),
        label: v?.name ?? `Verification ${vid}`,
        ref: v?.reference_code ?? `VER-${vid}`,
      },
    });
    yj += 1;
  }

  const edges: Edge[] = [];
  let ei = 0;
  for (const m of matrix) {
    if (m.project_id !== projectId) continue;
    if (!reqIds.has(m.req_id) || !verIds.has(m.verification_id)) continue;
    edges.push({
      id: `e-${ei++}`,
      source: `r-${m.req_id}`,
      target: `v-${m.verification_id}`,
      animated: m.suspect,
      style: m.suspect ? { stroke: '#f28b82', strokeWidth: 2 } : { stroke: '#8ab4f8', strokeWidth: 1.5 },
    });
  }

  return { nodes, edges };
}

export default function TraceabilityGraph({
  projectId,
  basePath,
}: {
  projectId: number;
  basePath: string;
}) {
  const [baseNodes, setBaseNodes] = useState<Node[]>([]);
  const [baseEdges, setBaseEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [matrix, requirements, allVer] = await Promise.all([
        listMatrix(projectId),
        listRequirements(projectId),
        listVerifications(),
      ]);
      const verifications = allVer.filter((v) => v.project_id === projectId);
      const { nodes: n, edges: e } = buildGraph(matrix, requirements, verifications, projectId);
      setBaseNodes(n);
      setBaseEdges(e);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelectedNodeId(null);
  }, [projectId]);

  useEffect(() => {
    if (selectedNodeId != null && !baseNodes.some((n) => n.id === selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [baseNodes, selectedNodeId]);

  const nodes = useMemo(
    () => highlightNodesForSelection(baseNodes, baseEdges, selectedNodeId),
    [baseNodes, baseEdges, selectedNodeId],
  );

  const edges = useMemo(
    () => highlightEdgesForSelection(baseEdges, selectedNodeId),
    [baseEdges, selectedNodeId],
  );

  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    setSelectedNodeId(node.id);
  }, []);

  const { onNodeDoubleClick } = useGraphNodeNavigation(basePath);

  const empty = useMemo(
    () => baseNodes.length === 0 && !loading && !err,
    [baseNodes.length, loading, err],
  );

  if (err) {
    return (
      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/25 text-red-200 text-sm">
        {err}
      </div>
    );
  }

  if (loading) {
    return (
      <ResizableGraphShell projectId={projectId} viewKey="coverage" placeholder>
        <span className="text-stitch-muted text-sm">Loading traceability graph…</span>
      </ResizableGraphShell>
    );
  }

  if (empty) {
    return (
      <ResizableGraphShell projectId={projectId} viewKey="coverage" placeholder>
        <div className="flex flex-col items-center text-stitch-muted text-sm">
          No matrix links in this project yet.
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 text-stitch-accent text-xs font-semibold hover:underline"
          >
            Retry
          </button>
        </div>
      </ResizableGraphShell>
    );
  }

  return (
    <ResizableGraphShell projectId={projectId} viewKey="coverage">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15, minZoom: 0.05 }}
        minZoom={0.05}
        maxZoom={2}
        nodesDraggable={false}
        selectNodesOnDrag={false}
        zoomOnDoubleClick={false}
        proOptions={{ hideAttribution: true }}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onPaneClick={() => setSelectedNodeId(null)}
      >
        <Background color="rgba(255,255,255,0.06)" gap={20} />
        <Controls />
      </ReactFlow>
    </ResizableGraphShell>
  );
}
